import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { useGql } from '@ah/design';
import { useTrack } from '@ah/analytics/shared';
import { Header, BottomSheet } from '@ah/design/mobile';
import { NotificationsBell } from '@ah/notifications/mobile';
import { MessagesBell } from '@ah/chat/mobile';
import { share } from '@ah/native/share';
import { LOGO_MARK_PNG } from '../assets';
import {
  MY_GATHERING_SERIES_QUERY,
  GATHERING_SERIES_CARD_QUERY,
  GATHERING_DETAIL_RESERVE_MUTATION,
  REGISTER_SERIES_INTEREST_MUTATION,
  DISMISS_GATHERING_SERIES_MUTATION,
  type GatheringSeries,
} from '@ah/gatherings/shared';
import {
  SUGGEST_HUMAN_Q,
  HUMAN_CARD_Q,
  RECORD_MATCH_DECISION_M,
  type NominationSuggestion,
} from '@ah/people/shared';
import {
  SUGGEST_CIRCLE_Q,
  SUGGEST_COALITION_Q,
  SUGGEST_LEAGUE_Q,
  JOIN_CIRCLE_MUTATION,
  DECLINE_CIRCLE_MUTATION,
  type Circle,
} from '@ah/circles/shared';
import {
  SUGGEST_VENUE_Q,
  RECORD_VENUE_DECISION_M,
  type Venue,
} from '@ah/venues/shared';
import { VenueCard, venueCardTitle } from '../cards/VenueCard';
import type { QuestionOption } from '@ah/questions/shared';
import {
  SUGGEST_QUESTION_Q,
  RECORD_ANSWER_M,
  type QuestionSuggestion,
  type FollowUpAnswerInput,
} from '@ah/questions/shared';
import {
  SUGGEST_TRIGGERS_Q,
  type TriggerSuggestion,
} from '@ah/people/shared';
import {
  SUGGEST_TOPIC_Q,
  RECORD_TOPIC_DECISION_M,
  type TopicSuggestion,
} from '@ah/topics/shared';
import { TopicCard } from '@ah/topics/mobile';
import {
  SUGGEST_INITIATIVE_Q,
  REGISTER_INITIATIVE_INTEREST_M,
  DECLINE_INITIATIVE_M,
  type Initiative,
} from '@ah/initiatives/shared';
import { InitiativeCard, InitiativeForm, type InitiativeFormValues } from '@ah/initiatives/mobile';
import { ONBOARDED_KEY } from './Onboarding';
import { useMe } from '../useAuth';
import type { ChosenSuperconnector } from '@ah/people/shared';

import { CARD_VARIANTS, TOSS_VARIANTS } from '../cards/_shared/motion';
import { readCachedSuperconnector } from '../cards/_shared/cachedSuperconnector';
import { CardHeader } from '../cards/_shared/CardHeader';
import { SuperconnectorAvatar } from '../cards/_shared/SuperconnectorAvatar';
import { SuperconnectorBubble } from '../cards/_shared/SuperconnectorBubble';
import { CardSkeleton } from '../cards/_shared/CardSkeleton';
import { PersonCard } from '../cards/PersonCard';
import { CircleCard } from '../cards/CircleCard';
import { CoalitionCard } from '../cards/CoalitionCard';
import { GatheringSeriesCard } from '../cards/GatheringSeriesCard';
import { QuestionCard, extractQuestionAction, questionNeedsContinue } from '../cards/QuestionCard';
import { MediaCard, BookReaderSheet, mediaCardTitle, type MediaItem, type NewsItem, type VideoItem, type BookItem, type PodcastItem } from '../cards/MediaCard';
import { TriggerCard } from '../cards/TriggerCard';
import { ConfirmContent, ConfirmFooter, type ConfirmState, type ConfirmOption } from '../cards/ConfirmSheet';
import { PerkCard, perkCardTitle, perksPool, type PerkItem } from '../cards/PerkCard';
import { MusicCard, musicCardTitle, type MusicItem } from '../cards/MusicCard';
import { RECOMMENDED_SONGS_QUERY, RECORD_SONG_SAVE_MUTATION } from '@ah/music/shared';
import { RECORD_STORY_SAVE_MUTATION } from '@ah/news/shared';
import { RECORD_EPISODE_SAVE_MUTATION } from '@ah/podcasts/shared';
import { RECORD_VIDEO_SAVE_MUTATION } from '@ah/videos/shared';
import { RECORD_BOOK_SAVE_MUTATION } from '@ah/books/shared';
import { BucketListCard, bucketListCardTitle } from '../cards/BucketListCard';
import { CanYouHelpCard, canYouHelpQuestion } from '../cards/CanYouHelpCard';
import { AppIdeaCard, appIdeaCardTitle } from '../cards/AppIdeaCard';
import {
  SUGGEST_BUCKET_LIST_ITEM_Q,
  RESPOND_BUCKET_LIST_ITEM_M,
  type BucketListItem,
} from '@ah/bucketlist/shared';
import {
  SUGGEST_APP_IDEA_Q,
  RESPOND_APP_IDEA_M,
  type AppIdea,
} from '@ah/appideas/shared';
import {
  SUGGEST_ASK_Q,
  SEND_ASK_M,
  DECLINE_ASK_M,
  type Ask,
} from '@ah/asks/shared';

// ─── Card model ─────────────────────────────────────────────────────────
type Card =
  | { kind: 'person'; key: string; data: NominationSuggestion }
  | { kind: 'circle'; key: string; data: Circle }
  | { kind: 'coalition'; key: string; data: Circle }
  | { kind: 'league'; key: string; data: Circle }
  | { kind: 'venue'; key: string; data: Venue }
  | { kind: 'question'; key: string; data: QuestionSuggestion }
  | { kind: 'event'; key: string; data: GatheringSeries }
  | { kind: 'topic'; key: string; data: TopicSuggestion }
  | { kind: 'initiative'; key: string; data: Initiative }
  | { kind: 'trigger'; key: string; data: TriggerSuggestion }
  | { kind: 'media'; key: string; data: MediaItem }
  | { kind: 'perk'; key: string; data: PerkItem }
  | { kind: 'music'; key: string; data: MusicItem }
  | { kind: 'bucket'; key: string; data: BucketListItem }
  | { kind: 'help'; key: string; data: Ask }
  | { kind: 'app'; key: string; data: AppIdea };

type Kind = Card['kind'];

// Best-effort {id, name} for analytics. Card data shapes vary across kinds —
// keep this loose rather than carrying an exhaustive switch that drifts every
// time a card type gains a new field.
function cardItemRef(c: Card): { id: string; name?: string } {
  const d = c.data as {
    id?: string;
    slug?: string;
    title?: string;
    name?: string;
    label?: string;
    shortTitle?: string;
    suggestedCopy?: string;
  };
  const id = d.id ?? d.slug ?? '';
  const name = d.title ?? d.name ?? d.label ?? d.shortTitle ?? d.suggestedCopy;
  return name ? { id, name } : { id };
}

// Target ratios. Higher number → more frequent. Cards are picked by weighted
// random among the kinds that aren't currently on cooldown / inflight, so the
// queue fills with whichever fetcher returns first. The live values come from
// the server (`appConfig('home_feed_weights')`) so the mix can be tuned without
// a mobile release; these defaults seed the first render and act as a fallback.
const DEFAULT_WEIGHTS: Record<Kind, number> = {
  person: 30,
  circle: 12,
  coalition: 8,
  league: 8,
  media: 12,
  topic: 8,
  venue: 8,
  bucket: 8,
  initiative: 6,
  question: 6,
  event: 12,
  app: 5,
  help: 5,
  perk: 0,
  trigger: 0,
  music: 3,
};

const HOME_FEED_WEIGHTS_Q = /* GraphQL */ `query { appConfig(key: "home_feed_weights") }`;

const QUEUE_TARGET = 5;
// If a fetcher returns nothing, back off this kind for a while before retrying.
const COOLDOWN_MS = 30_000;

// First-session gating: until the user has seen this many cards on this device,
// only surface "starter" kinds. Keeps the first impression human-centric —
// venues, questions, and triggers shouldn't be card #1.
const STARTER_KINDS: ReadonlySet<Kind> = new Set(['person', 'circle', 'event']);
const STARTER_GATE_COUNT = 6;
const CARDS_SHOWN_KEY = 'emissaries.cardsShown.v1';
function readCardsShown(): number {
  try {
    const v = Number(localStorage.getItem(CARDS_SHOWN_KEY));
    return Number.isFinite(v) && v >= 0 ? v : 0;
  } catch { return 0; }
}
function bumpCardsShown(): number {
  const next = readCardsShown() + 1;
  try { localStorage.setItem(CARDS_SHOWN_KEY, String(next)); } catch {}
  return next;
}

function buildShuffledMediaPool(
  liveNews: NewsItem[],
  liveVideos: VideoItem[],
  liveBooks: BookItem[],
  livePodcasts: PodcastItem[],
): MediaItem[] {
  return [...liveNews, ...liveVideos, ...liveBooks, ...livePodcasts].sort(() => Math.random() - 0.5);
}

const LIVE_MEDIA_Q = /* GraphQL */ `
  query HomeLiveMedia {
    newsStories(limit: 20) {
      id
      title
      snippet
      headlineImageUrl
      sourceName
      sourcePublishedAt
      articleCount
      topics { name id }
      summary {
        whatHappened
        eli5
        longRead
        keyQuotes { quote speaker sourceName }
        keyStats { stat context sourceName }
        keyPeople { name role }
        whyItMatters
        relatedTopics
        sourcesCited { title url sourceName }
      }
      articles { title snippet sourceName articleUrl publishedAt imageUrl }
    }
    videos(limit: 20) {
      id
      youtubeId
      title
      description
      imageUrl
      duration
      viewCount
      datePublished
      channel { title imageUrl handle }
      summary {
        hook
        whatItsAbout
        eli5
        longRead
        keyTakeaways
        keyConcepts { term wikipediaQuery why }
        keySpeakers { name bio }
        keyIdeas { title body }
        keyQuotes { quote speaker timestampSec }
        whyItMatters
        chapters { title startSec body }
        topics
      }
    }
    books(limit: 20) {
      id
      title
      subtitle
      description
      coverUrl
      firstPublishYear
      authors { name }
      summary {
        hook
        themes
        youllLikeThisIf
        readingMinutesEst
        chapters {
          title
          keyTakeaway
          blocks { kind body source }
        }
      }
    }
    podcastEpisodes(limit: 20) {
      id
      name
      description
      imageUrl
      duration
      datePublished
      audioUrl
      episodeNumber
      seasonNumber
      series { name author imageUrl }
      summary {
        hook
        whatItsAbout
        eli5
        longRead
        keyQuotes { quote speaker timestampSec }
        keyIdeas { title body }
        keyPeople { name role }
        whyItMatters
        chapters { title startSec body }
        topics
      }
    }
  }
`;

type LiveNewsRow = Omit<NewsItem, 'kind' | 'imageUrl' | 'publishedAt'> & {
  headlineImageUrl: string | null;
  sourcePublishedAt: string | null;
};
type LiveVideoRow = Omit<VideoItem, 'kind'>;
type LiveBookRow = Omit<BookItem, 'kind'>;
type LivePodcastRow = Omit<PodcastItem, 'kind' | 'title'> & { name: string };

function mapLiveNews(rows: LiveNewsRow[]): NewsItem[] {
  return rows.map((s) => ({
    kind: 'news',
    id: s.id,
    title: s.title,
    snippet: s.snippet,
    imageUrl: s.headlineImageUrl,
    sourceName: s.sourceName,
    topics: s.topics,
    articleCount: s.articleCount,
    publishedAt: s.sourcePublishedAt,
    summary: s.summary,
    articles: s.articles,
  }));
}
function mapLiveVideos(rows: LiveVideoRow[]): VideoItem[] {
  return rows.map((v) => ({ kind: 'video', ...v }));
}
function mapLiveBooks(rows: LiveBookRow[]): BookItem[] {
  return rows.map((b) => ({ kind: 'book', ...b }));
}
function mapLivePodcasts(rows: LivePodcastRow[]): PodcastItem[] {
  return rows.map(({ name, ...rest }) => ({ kind: 'podcast', title: name, ...rest }));
}

function pickWeightedKind(
  weights: Record<Kind, number>,
  skip: Set<Kind>,
  inflight: ReadonlySet<Kind>,
  lastKind: Kind | null,
  cardsShown: number,
): Kind | null {
  let candidates = (Object.keys(weights) as Kind[]).filter((k) => !skip.has(k));
  // First-session gate: restrict to starter kinds for the user's first few cards.
  if (cardsShown < STARTER_GATE_COUNT) {
    const starters = candidates.filter((k) => STARTER_KINDS.has(k));
    if (starters.length > 0) {
      candidates = starters;
    } else {
      // No starter available right now. If any starter is still in flight, wait
      // this tick rather than poison the first impression with a filler card.
      // Only fall through to non-starters if every starter is on cooldown (else
      // the queue could stall for COOLDOWN_MS).
      const anyStarterInflight = (Array.from(STARTER_KINDS) as Kind[]).some((k) => inflight.has(k));
      if (anyStarterInflight) return null;
    }
  }
  if (candidates.length === 0) return null;
  // Anti-repeat: bias away from the most recently surfaced kind unless it's
  // the only thing available.
  const usable = candidates.filter((k) => k !== lastKind);
  const pool = usable.length > 0 ? usable : candidates;
  const total = pool.reduce((s, k) => s + weights[k], 0);
  let r = Math.random() * total;
  for (const k of pool) {
    r -= weights[k];
    if (r <= 0) return k;
  }
  return pool[pool.length - 1] ?? null;
}

export function Home() {
  const gql = useGql();
  const track = useTrack({ surface: 'mobile' });
  const navigate = useNavigate();
  const { data: meData } = useMe();
  const godmother: ChosenSuperconnector | null =
    meData?.chosenSuperconnector ?? readCachedSuperconnector();

  useEffect(() => {
    // Wait until `me` resolves so we don't bounce a returning user (whose
    // server-side `onboardedAt` is set) through the walkthrough.
    if (meData === undefined) return;
    if (meData?.onboardedAt) return;
    try {
      if (localStorage.getItem(ONBOARDED_KEY)) return;
    } catch {}
    navigate('/onboarding', { replace: true });
  }, [navigate, meData]);

  // ─── Server-tunable card-kind weights ───────────────────────────────
  const { data: weightsData } = useQuery({
    queryKey: ['home_feed_weights'],
    queryFn: async () => {
      try {
        const r = await gql.request<{ appConfig: Partial<Record<string, number>> | null }>(HOME_FEED_WEIGHTS_Q);
        return r.appConfig ?? null;
      } catch {
        return null;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
  const weights = useMemo<Record<Kind, number>>(() => {
    if (!weightsData) return DEFAULT_WEIGHTS;
    const merged = { ...DEFAULT_WEIGHTS };
    for (const k of Object.keys(DEFAULT_WEIGHTS) as Kind[]) {
      const v = weightsData[k];
      if (typeof v === 'number' && v >= 0) merged[k] = v;
    }
    return merged;
  }, [weightsData]);
  const weightsRef = useRef(weights);
  weightsRef.current = weights;

  // ─── Single card queue ──────────────────────────────────────────────
  const [queue, setQueue] = useState<Card[]>([]);
  const [exhausted, setExhausted] = useState(false);
  const seenRef = useRef(new Set<string>());
  const inflightRef = useRef(new Set<Kind>());
  const cooldownUntilRef = useRef(new Map<Kind, number>());
  const lastKindRef = useRef<Kind | null>(null);

  // Cached batches (for endpoints that return many items at once).
  const mediaPoolRef = useRef<MediaItem[]>([]);
  const triggerPoolRef = useRef<TriggerSuggestion[]>([]);
  const eventPoolRef = useRef<GatheringSeries[]>([]);
  const liveNewsRef = useRef<NewsItem[]>([]);
  const liveVideosRef = useRef<VideoItem[]>([]);
  const liveBooksRef = useRef<BookItem[]>([]);
  const livePodcastsRef = useRef<PodcastItem[]>([]);
  // Hardcoded session-only pools for the remaining prototype card kinds.
  const perkPoolRef = useRef<PerkItem[]>([]);
  const musicPoolRef = useRef<MusicItem[]>([]);

  const mainRef = useRef<HTMLElement | null>(null);

  // ─── UI state ───────────────────────────────────────────────────────
  const [tossing, setTossing] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [bookReader, setBookReader] = useState<BookItem | null>(null);
  const [me, setMe] = useState<{ firstName: string | null }>({ firstName: null });

  // ─── Question script state ──────────────────────────────────────────────
  const [questionCurrentNodeId, setQuestionCurrentNodeId] = useState<string | null>(null);
  const [questionSelectedIds, setQuestionSelectedIds] = useState<Set<string>>(new Set());
  const [questionOtherText, setQuestionOtherText] = useState('');
  const [questionDone, setQuestionDone] = useState(false);
  const questionAnswerRef = useRef<{ rootSelected: string[]; rootOther: string | null; followUps: FollowUpAnswerInput[] } | null>(null);

  // ─── Sheets opened from cards ───────────────────────────────────────
  const [seriesSheetOpen, setSeriesSheetOpen] = useState(false);
  type SeriesStage = 'pick' | 'confirm_email' | 'reserving' | 'all_sold_out';
  const [seriesStage, setSeriesStage] = useState<SeriesStage>('pick');
  const [seriesSlot, setSeriesSlot] = useState<GatheringSeries['upcomingSessions'][number] | null>(null);
  const [seriesError, setSeriesError] = useState<string | null>(null);
  const [seriesNotifying, setSeriesNotifying] = useState(false);
  const [seriesEmail, setSeriesEmail] = useState('');
  const [seriesEmailError, setSeriesEmailError] = useState<string | null>(null);

  const [initiativeSheetOpen, setInitiativeSheetOpen] = useState(false);
  const [initiativeSubmitting, setInitiativeSubmitting] = useState(false);

  // Help card: share sheet (Web Share API with clipboard fallback).
  const [helpSheetOpen, setHelpSheetOpen] = useState(false);
  const [helpCopied, setHelpCopied] = useState(false);
  const [helpSharing, setHelpSharing] = useState(false);

  // Media card: yes -> 3-button bottom sheet (consume now / save / done).
  const [mediaSheet, setMediaSheet] = useState<MediaItem | null>(null);

  // Coalition join sheet: opens when the user taps Yes on a Coalition card.
  // Lets them pick which contribution dimensions they pledge.
  const [coalitionSheet, setCoalitionSheet] = useState<Circle | null>(null);
  const [coalitionSelected, setCoalitionSelected] = useState<Set<string>>(
    () => new Set(['skills', 'intros']),
  );
  const [coalitionOtherText, setCoalitionOtherText] = useState('');
  const [coalitionSubmitting, setCoalitionSubmitting] = useState(false);

  // League team-selection sheet: opens when the user taps Yes on a League card.
  // Lets them pick which of their alumni circles to represent on. All teams
  // are pre-selected by default — user un-ticks any they don't want.
  const [leagueSheet, setLeagueSheet] = useState<Circle | null>(null);
  const [leagueSelected, setLeagueSelected] = useState<Set<string>>(new Set());
  const [leagueSubmitting, setLeagueSubmitting] = useState(false);

  const card = queue[0] ?? null;
  const cardKey = card?.key ?? '_empty';

  useEffect(() => { mainRef.current?.scrollTo({ top: 0, behavior: 'auto' }); }, [cardKey]);

  // ─── Deep-link: ?card=<kind>:<id> ───────────────────────────────────
  // Fired when the user lands on Home from a re-engagement push. Fetches the
  // target card and prepends it to the queue so it becomes the active card,
  // then clears the param so a reload doesn't re-trigger.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const param = searchParams.get('card');
    if (!param) return;
    const colon = param.indexOf(':');
    if (colon <= 0) { setSearchParams({}, { replace: true }); return; }
    const kind = param.slice(0, colon);
    const id = param.slice(colon + 1);
    setSearchParams({}, { replace: true });

    let cancelled = false;
    (async () => {
      try {
        if (kind === 'person') {
          const r = await gql.request<{ humanCard: NominationSuggestion | null }>(HUMAN_CARD_Q, { id });
          const p = r.humanCard;
          if (!p || cancelled) return;
          const key = `person:${p.id}`;
          seenRef.current.add(key);
          setQueue((q) => [{ kind: 'person', key, data: p }, ...q.filter((c) => c.key !== key)]);
        } else if (kind === 'event') {
          const r = await gql.request<{ gatheringSeriesCard: GatheringSeries | null }>(GATHERING_SERIES_CARD_QUERY, { id });
          const e = r.gatheringSeriesCard;
          if (!e || cancelled) return;
          const key = `event:${e.id}`;
          seenRef.current.add(key);
          setQueue((q) => [{ kind: 'event', key, data: e }, ...q.filter((c) => c.key !== key)]);
        }
      } catch (err) {
        console.warn('[home] deep-linked card fetch failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [searchParams, setSearchParams, gql]);

  // ─── Fetchers ───────────────────────────────────────────────────────
  // Each returns 0 or more Cards. Static-pool kinds drain a local cache;
  // network kinds hit GraphQL. Order-of-arrival drives the queue.
  const fetchers: Record<Kind, () => Promise<Card[]>> = {
    person: async () => {
      try {
        const r = await gql.request<{ suggestHuman: NominationSuggestion | null }>(SUGGEST_HUMAN_Q);
        const p = r.suggestHuman;
        return p ? [{ kind: 'person', key: `person:${p.id}`, data: p }] : [];
      } catch { return []; }
    },
    circle: async () => {
      try {
        const r = await gql.request<{ suggestCircle: Circle | null }>(SUGGEST_CIRCLE_Q);
        const c = r.suggestCircle;
        return c ? [{ kind: 'circle', key: `circle:${c.id}`, data: c }] : [];
      } catch { return []; }
    },
    coalition: async () => {
      try {
        const r = await gql.request<{ suggestCoalition: Circle | null }>(SUGGEST_COALITION_Q);
        const c = r.suggestCoalition;
        return c ? [{ kind: 'coalition', key: `coalition:${c.id}`, data: c }] : [];
      } catch { return []; }
    },
    league: async () => {
      try {
        const r = await gql.request<{ suggestLeague: Circle | null }>(SUGGEST_LEAGUE_Q);
        const c = r.suggestLeague;
        return c ? [{ kind: 'league', key: `league:${c.id}`, data: c }] : [];
      } catch { return []; }
    },
    venue: async () => {
      try {
        const r = await gql.request<{ suggestVenue: Venue | null }>(SUGGEST_VENUE_Q);
        const v = r.suggestVenue;
        return v ? [{ kind: 'venue', key: `venue:${v.id}`, data: v }] : [];
      } catch { return []; }
    },
    question: async () => {
      try {
        const r = await gql.request<{ suggestQuestion: QuestionSuggestion | null }>(SUGGEST_QUESTION_Q);
        const g = r.suggestQuestion;
        return g ? [{ kind: 'question', key: `question:${g.id}`, data: g }] : [];
      } catch { return []; }
    },
    event: async () => {
      if (eventPoolRef.current.length === 0) {
        try {
          const r = await gql.request<{ myGatheringSeries: GatheringSeries[] }>(MY_GATHERING_SERIES_QUERY);
          eventPoolRef.current = r.myGatheringSeries ?? [];
        } catch { return []; }
      }
      const e = eventPoolRef.current.shift();
      return e ? [{ kind: 'event', key: `event:${e.id}`, data: e }] : [];
    },
    topic: async () => {
      try {
        const r = await gql.request<{ suggestTopic: TopicSuggestion | null }>(SUGGEST_TOPIC_Q);
        const t = r.suggestTopic;
        return t ? [{ kind: 'topic', key: `topic:${t.id}`, data: t }] : [];
      } catch { return []; }
    },
    initiative: async () => {
      try {
        const r = await gql.request<{ suggestInitiative: Initiative | null }>(SUGGEST_INITIATIVE_Q);
        const i = r.suggestInitiative;
        return i ? [{ kind: 'initiative', key: `initiative:${i.slug}`, data: i }] : [];
      } catch { return []; }
    },
    trigger: async () => {
      if (triggerPoolRef.current.length === 0) {
        try {
          const r = await gql.request<{ suggestTriggers: TriggerSuggestion[] }>(SUGGEST_TRIGGERS_Q);
          triggerPoolRef.current = (r.suggestTriggers ?? []).slice().sort(() => Math.random() - 0.5);
        } catch { return []; }
      }
      const t = triggerPoolRef.current.shift();
      return t ? [{ kind: 'trigger', key: `trigger:${t.id}`, data: t }] : [];
    },
    media: async () => {
      if (mediaPoolRef.current.length === 0) {
        mediaPoolRef.current = buildShuffledMediaPool(
          liveNewsRef.current,
          liveVideosRef.current,
          liveBooksRef.current,
          livePodcastsRef.current,
        );
      }
      const m = mediaPoolRef.current.shift();
      return m ? [{ kind: 'media', key: `media:${m.id}`, data: m }] : [];
    },
    perk: async () => {
      if (perkPoolRef.current.length === 0) {
        perkPoolRef.current = perksPool.slice().sort(() => Math.random() - 0.5);
      }
      const p = perkPoolRef.current.shift();
      return p ? [{ kind: 'perk', key: `perk:${p.id}`, data: p }] : [];
    },
    music: async () => {
      if (musicPoolRef.current.length === 0) {
        try {
          const r = await gql.request<{ recommendedSongs: MusicItem[] }>(RECOMMENDED_SONGS_QUERY, { limit: 20 });
          musicPoolRef.current = (r.recommendedSongs ?? [])
            .filter((s) => !!s.variantAAudioUrl)
            .sort(() => Math.random() - 0.5);
        } catch { return []; }
      }
      const m = musicPoolRef.current.shift();
      return m ? [{ kind: 'music', key: `music:${m.id}`, data: m }] : [];
    },
    bucket: async () => {
      try {
        const r = await gql.request<{ suggestBucketListItem: BucketListItem | null }>(SUGGEST_BUCKET_LIST_ITEM_Q);
        const b = r.suggestBucketListItem;
        return b ? [{ kind: 'bucket', key: `bucket:${b.id}`, data: b }] : [];
      } catch { return []; }
    },
    help: async () => {
      try {
        const r = await gql.request<{ suggestAsk: Ask | null }>(SUGGEST_ASK_Q);
        const h = r.suggestAsk;
        return h ? [{ kind: 'help', key: `help:${h.id}`, data: h }] : [];
      } catch { return []; }
    },
    app: async () => {
      try {
        const r = await gql.request<{ suggestAppIdea: AppIdea | null }>(SUGGEST_APP_IDEA_Q);
        const a = r.suggestAppIdea;
        return a ? [{ kind: 'app', key: `app:${a.id}`, data: a }] : [];
      } catch { return []; }
    },
  };

  // ─── Top-up loop ────────────────────────────────────────────────────
  // While we're below target and not all kinds are skipped, fire fetches in
  // parallel. Each fetch appends to the queue when it returns. The queue is
  // never blocked on any one slow kind.
  const topUp = useCallback(() => {
    setQueue((q) => {
      const slack = QUEUE_TARGET - q.length - inflightRef.current.size;
      if (slack <= 0) return q;
      const now = Date.now();
      for (let i = 0; i < slack; i++) {
        const skip = new Set<Kind>([
          ...inflightRef.current,
          ...Array.from(cooldownUntilRef.current.entries())
            .filter(([, until]) => until > now)
            .map(([k]) => k),
        ]);
        const kind = pickWeightedKind(weightsRef.current, skip, inflightRef.current, lastKindRef.current, readCardsShown());
        if (!kind) break;
        inflightRef.current.add(kind);
        void fetchers[kind]().then((cards) => {
          inflightRef.current.delete(kind);
          const fresh = cards.filter((c) => !seenRef.current.has(c.key));
          if (fresh.length === 0) {
            cooldownUntilRef.current.set(kind, Date.now() + COOLDOWN_MS);
            setQueue((curr) => {
              if (curr.length === 0 && inflightRef.current.size === 0) {
                const allCold = (Object.keys(weightsRef.current) as Kind[]).every(
                  (k) => (cooldownUntilRef.current.get(k) ?? 0) > Date.now(),
                );
                if (allCold) setExhausted(true);
              }
              return curr;
            });
            return;
          }
          fresh.forEach((c) => seenRef.current.add(c.key));
          setQueue((curr) => [...curr, ...fresh]);
          setExhausted(false);
        });
      }
      return q;
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { topUp(); }, [topUp]);
  // Keep topping up after each consumption.
  useEffect(() => { topUp(); }, [queue.length, topUp]);

  // Pull live news + videos once on mount. The media fetcher reads these refs
  // when (re)building its shuffled pool — until they arrive, falls back to the
  // static JSON snapshot, so the queue is never blocked on this fetch.
  useEffect(() => {
    let cancelled = false;
    gql.request<{
      newsStories: LiveNewsRow[];
      videos: LiveVideoRow[];
      books: LiveBookRow[];
      podcastEpisodes: LivePodcastRow[];
    }>(LIVE_MEDIA_Q)
      .then((r) => {
        if (cancelled) return;
        liveNewsRef.current = mapLiveNews(r.newsStories ?? []);
        liveVideosRef.current = mapLiveVideos(r.videos ?? []);
        liveBooksRef.current = mapLiveBooks(r.books ?? []);
        livePodcastsRef.current = mapLivePodcasts(r.podcastEpisodes ?? []);
        // Force the next media draw to rebuild from the new live pool.
        mediaPoolRef.current = [];
      })
      .catch((err) => console.error('[home] live media fetch failed:', err));
    return () => { cancelled = true; };
  }, [gql]);

  // ─── Advance ────────────────────────────────────────────────────────
  function advance() {
    if (card) {
      lastKindRef.current = card.kind;
      bumpCardsShown();
    }
    setQueue((q) => q.slice(1));
  }

  // ─── Question script (Yes flow opens the script in-card) ────────────────
  const activeQuestion = card?.kind === 'question' ? card.data : null;
  useEffect(() => {
    setQuestionSelectedIds(new Set());
    setQuestionOtherText('');
    setQuestionDone(false);
    setQuestionCurrentNodeId(activeQuestion?.script?.rootId ?? null);
    questionAnswerRef.current = activeQuestion ? { rootSelected: [], rootOther: null, followUps: [] } : null;
  }, [activeQuestion]);

  const questionScript = activeQuestion?.script ?? null;
  const questionCurrentNode = questionScript && questionCurrentNodeId
    ? questionScript.nodes.find((n) => n.id === questionCurrentNodeId) ?? null
    : null;

  function captureNodeAnswer(nodeId: string, selectedIds: string[], otherText: string) {
    if (!activeQuestion || !questionAnswerRef.current) return;
    const isRoot = nodeId === activeQuestion.script.rootId;
    const includesOther = selectedIds.includes('__other__');
    const cleaned = selectedIds.filter((id) => id !== '__other__');
    const trimmed = otherText.trim();
    const other = includesOther && trimmed ? trimmed : null;
    if (isRoot) {
      questionAnswerRef.current.rootSelected = cleaned;
      questionAnswerRef.current.rootOther = other;
    } else {
      questionAnswerRef.current.followUps.push({ nodeId, optionIds: cleaned, freeText: other ?? undefined });
    }
  }

  function goToNext(nextId: string | null) {
    if (!nextId) { setQuestionDone(true); setQuestionCurrentNodeId(null); return; }
    setQuestionCurrentNodeId(nextId);
    setQuestionSelectedIds(new Set());
    setQuestionOtherText('');
  }

  function handleQuestionOptionTap(opt: QuestionOption) {
    if (!questionCurrentNode) return;
    if (questionCurrentNode.kind === 'RADIO') {
      setQuestionSelectedIds(new Set([opt.id]));
      captureNodeAnswer(questionCurrentNode.id, [opt.id], '');
      setTimeout(() => goToNext(opt.nextNodeId), 120);
      return;
    }
    setQuestionSelectedIds((s) => {
      const next = new Set(s);
      if (next.has(opt.id)) next.delete(opt.id); else next.add(opt.id);
      return next;
    });
  }

  function handleQuestionOtherTap() {
    setQuestionSelectedIds((s) => {
      const next = new Set(s);
      if (next.has('__other__')) next.delete('__other__'); else next.add('__other__');
      return next;
    });
  }

  function handleQuestionContinue() {
    if (questionDone) { finishQuestion(); return; }
    if (!questionCurrentNode) return;
    captureNodeAnswer(questionCurrentNode.id, Array.from(questionSelectedIds), questionOtherText);
    const selected = questionCurrentNode.options.filter((o) => questionSelectedIds.has(o.id));
    const nextId = selected.map((o) => o.nextNodeId).find((id) => !!id) ?? null;
    goToNext(nextId);
  }

  function persistQuestionOption() {
    if (!activeQuestion || !questionAnswerRef.current) return;
    const a = questionAnswerRef.current;
    if (a.rootSelected.length === 0 && !a.rootOther) return;
    void gql.request(RECORD_ANSWER_M, {
      input: {
        generatedQuestionId: activeQuestion.id,
        selectedOptionIds: a.rootSelected,
        freeTextAnswer: a.rootOther ?? undefined,
        followUpAnswers: a.followUps.length > 0 ? a.followUps : undefined,
      },
    }).catch(() => {});
  }

  function finishQuestion() {
    persistQuestionOption();
    advance();
  }

  useEffect(() => {
    if (!questionDone) return;
    const t = setTimeout(() => finishQuestion(), 1100);
    return () => clearTimeout(t);
  }, [questionDone]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Yes / No dispatch ──────────────────────────────────────────────
  function trackCardResponse(c: Card, response: 'yes' | 'no') {
    track('card_response', {
      cardKind: c.kind,
      response,
      item: cardItemRef(c),
    });
  }

  function onYes() {
    if (!card) return;
    trackCardResponse(card, 'yes');
    switch (card.kind) {
      case 'person': {
        const p = card.data;
        void gql.request(RECORD_MATCH_DECISION_M, { targetId: p.id, decision: 'yes' }).catch(() => {});
        const targetFirst = p.name?.split(' ')[0] ?? 'them';
        setConfirm({
          title: `Noted. How would you like to meet ${targetFirst}?`,
          message: "I'll do my best to make it happen.",
          options: [
            { id: 'gathering', label: 'At an Underlings gathering', defaultChecked: true },
            { id: 'one-on-one', label: '1:1 coffee / call' },
            { id: 'other', label: 'Other', allowFreeform: true },
          ],
          closeLabel: 'Done',
          onConfirm: () => { setConfirm(null); advance(); },
          onClose: () => setConfirm(null),
        });
        return;
      }
      case 'coalition': {
        setCoalitionSheet(card.data);
        setCoalitionSelected(new Set(['skills', 'intros']));
        return;
      }
      case 'league': {
        // Leagues only ask which alumni team(s) the member will represent.
        // No host/member role picker — the league framing assumes you're
        // playing. If the user has no candidate alumni teams, fall back to
        // joining directly with no team picks.
        const circle = card.data;
        const teams = circle.candidateTeams ?? [];
        if (teams.length === 0) {
          void gql.request(JOIN_CIRCLE_MUTATION, { circleId: circle.id }).catch(() => {});
          setConfirm({
            message: "Done — you're in. We'll let you know once a team forms.",
            closeLabel: 'Done',
            onConfirm: () => { setConfirm(null); advance(); },
            onClose: () => setConfirm(null),
          });
          return;
        }
        setLeagueSheet(circle);
        setLeagueSelected(new Set(teams.map((t) => t.circleId)));
        return;
      }
      case 'circle': {
        const circle = card.data;
        const teams = circle.candidateTeams ?? [];
        const showTeams = circle.alumniTeamsEnabled && teams.length > 0;

        const roleOptions: ConfirmOption[] = [
          { id: 'role:member', label: 'As a member / participant', defaultChecked: true },
          { id: 'role:host', label: 'As a host / leader' },
          { id: 'role:other', label: 'Other', allowFreeform: true },
        ];
        const teamOptions: ConfirmOption[] = teams.map((t, i) => ({
          id: `team:${t.circleId}`,
          label: t.existingMemberCount > 0
            ? `${t.name} (${t.existingMemberCount} already in)`
            : t.name,
          defaultChecked: true,
          ...(i === 0 ? { sectionHeading: 'Which alumni team will you represent?' } : {}),
        }));

        if (!showTeams) {
          void gql.request(JOIN_CIRCLE_MUTATION, { circleId: circle.id }).catch(() => {});
          setConfirm({
            title: 'Done. How would you like to get involved?',
            options: roleOptions,
            closeLabel: 'Done',
            onConfirm: () => { setConfirm(null); advance(); },
            onClose: () => setConfirm(null),
          });
          return;
        }

        setConfirm({
          title: 'Done. How would you like to get involved?',
          options: [...roleOptions, ...teamOptions],
          submitLabel: 'Done',
          onSubmit: (selected) => {
            const teamCircleIds = selected
              .filter((id) => id.startsWith('team:'))
              .map((id) => id.slice('team:'.length));
            void gql.request(JOIN_CIRCLE_MUTATION, { circleId: circle.id, teamCircleIds }).catch(() => {});
            setConfirm(null);
            advance();
          },
          onClose: () => setConfirm(null),
        });
        return;
      }
      case 'venue': {
        void gql.request(RECORD_VENUE_DECISION_M, { venueId: card.data.id, decision: 'yes' }).catch(() => {});
        setConfirm({
          title: 'Noted',
          message: "I'll invite you to gatherings at this venue — with friends, and people you should meet.",
          closeLabel: 'Done',
          onConfirm: () => { setConfirm(null); advance(); },
          onClose: () => setConfirm(null),
        });
        return;
      }
      case 'question': {
        const action = extractQuestionAction(card.data.suggestedCopy);
        setConfirm({
          message: `Great! I've noted that you'd like me to help you ${action}. Do you have 2 minutes to answer a few clarifying questions?`,
          primaryAction: { label: 'Answer', onClick: () => setConfirm(null) },
          onClose: () => setConfirm(null),
        });
        return;
      }
      case 'event': {
        const upcoming = card.data.upcomingSessions ?? [];
        const joinable = upcoming.filter((s) => s.capacity == null || s.participantCount < s.capacity);
        setSeriesSlot(null);
        setSeriesError(null);
        setSeriesStage(joinable.length === 0 ? 'all_sold_out' : 'pick');
        setSeriesSheetOpen(true);
        return;
      }
      case 'topic': {
        void gql.request(RECORD_TOPIC_DECISION_M, { topicId: card.data.id, decision: 'yes' }).catch(() => {});
        setConfirm({
          message: "Got it — I'll invite you to gatherings that discuss this topic.",
          closeLabel: 'Done',
          onConfirm: () => { setConfirm(null); advance(); },
          onClose: () => setConfirm(null),
        });
        return;
      }
      case 'initiative': {
        setInitiativeSheetOpen(true);
        return;
      }
      case 'trigger': {
        const label = card.data.label;
        setConfirm({
          message: `Done — I'll run "${label}" on the schedule you can tweak from Profile.`,
          closeLabel: 'Done',
          onConfirm: () => { setConfirm(null); advance(); },
          onClose: () => setConfirm(null),
        });
        return;
      }
      case 'media': {
        setMediaSheet(card.data);
        return;
      }
      case 'perk': {
        setConfirm({
          message: "Done — I'll email you details on how to claim it shortly.",
          closeLabel: 'Done',
          onConfirm: () => { setConfirm(null); advance(); },
          onClose: () => setConfirm(null),
        });
        return;
      }
      case 'music': {
        void gql.request(RECORD_SONG_SAVE_MUTATION, { id: card.data.id }).catch(() => {});
        advance();
        return;
      }
      case 'bucket': {
        void gql.request(RESPOND_BUCKET_LIST_ITEM_M, { itemId: card.data.id, status: 'added' }).catch(() => {});
        setConfirm({
          message: "Added to your bucket list. I'll keep an eye out for paths that might help you get there.",
          closeLabel: 'Done',
          onConfirm: () => { setConfirm(null); advance(); },
          onClose: () => setConfirm(null),
        });
        return;
      }
      case 'help': {
        setHelpCopied(false);
        setHelpSheetOpen(true);
        return;
      }
      case 'app': {
        void gql.request(RESPOND_APP_IDEA_M, { ideaId: card.data.id, status: 'yes' }).catch(() => {});
        setConfirm({
          message: "Logged. If enough members say yes to this one, we'll build it.",
          closeLabel: 'Done',
          onConfirm: () => { setConfirm(null); advance(); },
          onClose: () => setConfirm(null),
        });
        return;
      }
    }
  }

  function onNo() {
    if (!card) return;
    trackCardResponse(card, 'no');
    setTossing(true);
    switch (card.kind) {
      case 'person':
        void gql.request(RECORD_MATCH_DECISION_M, { targetId: card.data.id, decision: 'no' }).catch(() => {});
        break;
      case 'circle':
      case 'coalition':
      case 'league':
        void gql.request(DECLINE_CIRCLE_MUTATION, { circleId: card.data.id }).catch(() => {});
        break;
      case 'venue':
        void gql.request(RECORD_VENUE_DECISION_M, { venueId: card.data.id, decision: 'no' }).catch(() => {});
        break;
      case 'question':
        break;
      case 'topic':
        void gql.request(RECORD_TOPIC_DECISION_M, { topicId: card.data.id, decision: 'no' }).catch(() => {});
        break;
      case 'initiative':
        void gql.request(DECLINE_INITIATIVE_M, { slug: card.data.slug }).catch(() => {});
        break;
      case 'event':
        void gql.request(DISMISS_GATHERING_SERIES_MUTATION, { seriesId: card.data.id }).catch(() => {});
        break;
      case 'bucket':
        void gql.request(RESPOND_BUCKET_LIST_ITEM_M, { itemId: card.data.id, status: 'declined' }).catch(() => {});
        break;
      case 'app':
        void gql.request(RESPOND_APP_IDEA_M, { ideaId: card.data.id, status: 'no' }).catch(() => {});
        break;
      case 'help':
        void gql.request(DECLINE_ASK_M, { askId: card.data.id }).catch(() => {});
        break;
      // trigger, media, perk, music: client-side dismiss only.
    }
    advance();
  }

  // ─── Event slot picker handlers ─────────────────────────────────────
  function handleSelectSlot(slot: GatheringSeries['upcomingSessions'][number]) {
    if (slot.capacity != null && slot.participantCount >= slot.capacity) return;
    setSeriesSlot(slot);
    setSeriesEmail(meData?.email ?? '');
    setSeriesEmailError(null);
    setSeriesStage('confirm_email');
  }

  async function reserveSelectedSlot(email: string | null) {
    const slot = seriesSlot;
    if (!slot) return;
    setSeriesStage('reserving');
    setSeriesError(null);
    try {
      await gql.request(GATHERING_DETAIL_RESERVE_MUTATION, { gatheringId: slot.id, email });
      if (slot.externalLink) {
        try { window.open(slot.externalLink, '_blank', 'noopener,noreferrer'); } catch {}
      }
      closeSeriesSheetAndAdvance();
    } catch (err) {
      setSeriesError(err instanceof Error ? err.message : 'Could not reserve. Please try again.');
      setSeriesStage('confirm_email');
    }
  }

  async function handleConfirmEmail() {
    const trimmed = seriesEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setSeriesEmailError('Please enter a valid email address.');
      return;
    }
    setSeriesEmailError(null);
    await reserveSelectedSlot(trimmed);
  }

  function resetSeriesSheet() {
    setSeriesSheetOpen(false);
    setSeriesStage('pick');
    setSeriesSlot(null);
    setSeriesError(null);
    setSeriesNotifying(false);
    setSeriesEmail('');
    setSeriesEmailError(null);
  }

  // The user has either reserved a slot or asked to be notified — we're done
  // with this card, so advance the queue.
  function closeSeriesSheetAndAdvance() {
    resetSeriesSheet();
    setTossing(true);
    advance();
  }

  // Backdrop tap / swipe-down: always just dismiss without advancing — the
  // user gets to stay on the same card. Advancing happens via the primary
  // action button after a successful RSVP / Notify-me.
  function dismissSeriesSheet() {
    resetSeriesSheet();
  }

  async function handleNotifyMe() {
    if (card?.kind !== 'event' || seriesNotifying) return;
    const seriesId = card.data.id;
    setSeriesNotifying(true);
    setSeriesError(null);
    try {
      await gql.request(REGISTER_SERIES_INTEREST_MUTATION, { seriesId });
      closeSeriesSheetAndAdvance();
    } catch (err) {
      setSeriesError(err instanceof Error ? err.message : 'Could not save. Please try again.');
    } finally {
      setSeriesNotifying(false);
    }
  }

  // ─── Help-card share handler ────────────────────────────────────────
  // Routes through @ah/native/share — Capacitor's native UIActivityViewController
  // on iOS / ACTION_SEND on Android, Web Share API on browser, with a clipboard
  // fallback when neither is available. Either way we record the response
  // server-side and advance.
  async function handleHelpShare() {
    if (card?.kind !== 'help' || helpSharing) return;
    const ask = card.data;
    setHelpSharing(true);
    const shareText = ask.shareBody;
    const shareUrl = ask.shareUrl;

    const result = await share({ title: ask.shortTitle, text: shareText, url: shareUrl });

    let userShared = false;
    if (result.ok && result.shared) {
      userShared = true;
    } else if (result.ok && !result.shared) {
      // User opened the sheet but cancelled — don't record, leave sheet open.
      setHelpSharing(false);
      return;
    } else {
      // No share path available (or it threw) — clipboard fallback.
      try {
        await navigator.clipboard.writeText(`${shareText}\n\n${shareUrl}`);
        setHelpCopied(true);
      } catch {
        // If clipboard write also fails we still record + advance so the
        // user isn't stuck; they can copy from the visible URL.
        setHelpCopied(true);
      }
    }

    void gql.request(SEND_ASK_M, { askId: ask.id, sentBody: shareText }).catch(() => {});
    setHelpSharing(false);

    if (userShared) {
      setHelpSheetOpen(false);
      advance();
    }
    // For the clipboard path, leave the sheet open so the user can paste
    // and see the "Copied" hint; closing the sheet advances.
  }

  function closeHelpSheetAndAdvance() {
    setHelpSheetOpen(false);
    setHelpCopied(false);
    if (card?.kind === 'help') advance();
  }

  // ─── Media yes-sheet handlers ───────────────────────────────────────
  // Tapping Yes on a media card opens a sheet with three buttons: a
  // primary "Done" that just records the save and advances; a secondary
  // "Save for later" that does the same and closes; and a secondary
  // "Read/Watch/Listen now" that takes the user to the detail screen.
  function recordMediaSave(media: MediaItem) {
    switch (media.kind) {
      case 'book':
        void gql.request(RECORD_BOOK_SAVE_MUTATION, { id: media.id }).catch(() => {});
        return;
      case 'news':
        void gql.request(RECORD_STORY_SAVE_MUTATION, { id: media.id }).catch(() => {});
        return;
      case 'podcast':
        void gql.request(RECORD_EPISODE_SAVE_MUTATION, { id: media.id }).catch(() => {});
        return;
      case 'video':
        void gql.request(RECORD_VIDEO_SAVE_MUTATION, { id: media.id }).catch(() => {});
        return;
    }
  }
  function mediaConsumeLabel(media: MediaItem | null): string {
    switch (media?.kind) {
      case 'book': return 'Read now';
      case 'news': return 'Read now';
      case 'podcast': return 'Listen now';
      case 'video': return 'Watch now';
      default: return 'Open now';
    }
  }
  function handleMediaConsume() {
    const m = mediaSheet;
    if (!m) return;
    recordMediaSave(m);
    setMediaSheet(null);
    switch (m.kind) {
      case 'book':
        setBookReader(m);
        break;
      case 'news':
        navigate(`/news/${m.id}`);
        break;
      case 'podcast':
        navigate(`/podcasts/${m.id}`);
        break;
      case 'video':
        navigate(`/videos/${m.id}`);
        break;
    }
    advance();
  }
  function handleMediaSaveForLater() {
    const m = mediaSheet;
    if (!m) return;
    recordMediaSave(m);
    setMediaSheet(null);
    advance();
  }
  function handleMediaDone() {
    const m = mediaSheet;
    if (!m) return;
    recordMediaSave(m);
    setMediaSheet(null);
    advance();
  }

  // ─── Coalition contribution sheet handlers ──────────────────────────
  function toggleCoalitionOption(id: string) {
    setCoalitionSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleCoalitionSubmit() {
    const coalition = coalitionSheet;
    if (!coalition) return;
    setCoalitionSubmitting(true);
    const note = coalitionSelected.has('other') ? coalitionOtherText.trim() : '';
    void gql
      .request(JOIN_CIRCLE_MUTATION, {
        circleId: coalition.id,
        contributionOptions: Array.from(coalitionSelected),
        contributionNote: note ? note : null,
      })
      .catch(() => {})
      .finally(() => {
        setCoalitionSubmitting(false);
        setCoalitionSheet(null);
        setCoalitionSelected(new Set(['skills', 'intros']));
        setCoalitionOtherText('');
        advance();
      });
  }

  // ─── League team-selection sheet handlers ───────────────────────────
  function toggleLeagueTeam(circleId: string) {
    setLeagueSelected((prev) => {
      const next = new Set(prev);
      if (next.has(circleId)) next.delete(circleId);
      else next.add(circleId);
      return next;
    });
  }

  function submitLeagueJoin(teamCircleIds: string[]) {
    const league = leagueSheet;
    if (!league) return;
    setLeagueSubmitting(true);
    void gql
      .request(JOIN_CIRCLE_MUTATION, { circleId: league.id, teamCircleIds })
      .catch(() => {})
      .finally(() => {
        setLeagueSubmitting(false);
        setLeagueSheet(null);
        setLeagueSelected(new Set());
        advance();
      });
  }

  function handleLeagueConfirm() {
    submitLeagueJoin(Array.from(leagueSelected));
  }

  // ─── Initiative form handlers ───────────────────────────────────────
  function handleSubmitInitiative(values: InitiativeFormValues) {
    if (card?.kind !== 'initiative') return;
    const slug = card.data.slug;
    const confirmation = card.data.confirmation;
    const formData = card.data.formFields.length > 0 ? JSON.stringify(values) : null;
    setInitiativeSubmitting(true);
    void gql
      .request(REGISTER_INITIATIVE_INTEREST_M, { slug, formData })
      .catch(() => {})
      .finally(() => {
        setInitiativeSubmitting(false);
        setInitiativeSheetOpen(false);
        setConfirm({
          message: confirmation ?? "Got it — I'll be in touch.",
          closeLabel: 'Done',
          onConfirm: () => { setConfirm(null); advance(); },
          onClose: () => setConfirm(null),
        });
      });
  }

  // ─── Cached first name ──────────────────────────────────────────────
  useEffect(() => {
    try {
      const cached = localStorage.getItem('me.firstName');
      if (cached) setMe({ firstName: cached });
    } catch {}
  }, []);

  // ─── Render: header/title for the active card ───────────────────────
  function renderCard(c: Card) {
    switch (c.kind) {
      case 'person': {
        const firstName = c.data.name?.split(' ')[0] ?? null;
        return (
          <>
            <CardHeader godmother={godmother} title={`Would you like to meet ${firstName ?? 'them'}?`} />
            <PersonCard person={c.data} />
          </>
        );
      }
      case 'circle':
        return (
          <>
            <CardHeader godmother={godmother} title="Would you like to join this circle?" />
            <CircleCard circle={c.data} />
          </>
        );
      case 'coalition':
        return (
          <>
            <CardHeader godmother={godmother} title="Would you like to join this Underlings coalition?" />
            <CoalitionCard coalition={c.data} />
          </>
        );
      case 'league':
        return (
          <>
            <CardHeader godmother={godmother} title="Would you like to join this Underlings league?" />
            <CircleCard circle={c.data} />
          </>
        );
      case 'venue':
        return (
          <>
            <CardHeader godmother={godmother} title={venueCardTitle(c.data)} />
            <VenueCard venue={c.data} />
          </>
        );
      case 'question':
        return (
          <QuestionCard
            godmother={godmother}
            rootQuestion={c.data.suggestedCopy}
            current={questionCurrentNode}
            loading={!questionCurrentNode && !questionDone}
            done={questionDone}
            selectedIds={questionSelectedIds}
            otherText={questionOtherText}
            onOtherTextChange={setQuestionOtherText}
            onSelect={handleQuestionOptionTap}
            onOtherTap={handleQuestionOtherTap}
          />
        );
      case 'event':
        return (
          <>
            <CardHeader godmother={godmother} title="Would you like to attend this gathering?" />
            <GatheringSeriesCard series={c.data} />
          </>
        );
      case 'topic':
        return (
          <>
            <CardHeader godmother={godmother} title="Would you enjoy discussing this topic?" />
            <TopicCard topic={c.data} />
          </>
        );
      case 'initiative':
        return (
          <>
            <CardHeader godmother={godmother} title="Would you like to join this initiative?" />
            <InitiativeCard initiative={c.data} />
          </>
        );
      case 'trigger':
        return (
          <>
            <CardHeader godmother={godmother} title="Would you like to activate this standing wish?" />
            <TriggerCard trigger={c.data} />
          </>
        );
      case 'media':
        return (
          <>
            <CardHeader godmother={godmother} title={mediaCardTitle(c.data)} />
            <MediaCard item={c.data} onReadBook={setBookReader} />
          </>
        );
      case 'perk':
        return (
          <>
            <CardHeader godmother={godmother} title={perkCardTitle(c.data)} />
            <PerkCard item={c.data} />
          </>
        );
      case 'music':
        return (
          <>
            <CardHeader godmother={godmother} title={musicCardTitle(c.data)} />
            <MusicCard item={c.data} />
          </>
        );
      case 'bucket':
        return (
          <>
            <CardHeader godmother={godmother} title={bucketListCardTitle(c.data)} />
            <BucketListCard item={c.data} />
          </>
        );
      case 'help':
        return (
          <>
            <CardHeader godmother={godmother} title={canYouHelpQuestion} />
            <CanYouHelpCard item={c.data} />
          </>
        );
      case 'app':
        return (
          <>
            <CardHeader godmother={godmother} title={appIdeaCardTitle(c.data)} />
            <AppIdeaCard item={c.data} />
          </>
        );
    }
  }

  // ─── Footer: most cards = Yes/No, question = Skip only ──────────────────
  function renderFooter() {
    if (!card) {
      return (
        <div className="flex items-center gap-3 py-3">
          <button type="button" disabled className="flex-1 flex items-center justify-center h-12 rounded-full bg-primary text-primary-foreground font-semibold text-sm disabled:opacity-40 shadow-md shadow-primary/20">Yes</button>
          <button type="button" disabled className="flex items-center justify-center px-6 h-12 rounded-full border border-border bg-card text-sm font-medium text-muted-foreground disabled:opacity-40">No</button>
        </div>
      );
    }
    if (card.kind === 'question') {
      const continueDisabled = questionDone || !questionCurrentNode || questionSelectedIds.size === 0;
      return (
        <div className="flex items-center gap-3 py-3">
          <button
            type="button"
            onClick={handleQuestionContinue}
            disabled={continueDisabled}
            className="flex-1 flex items-center justify-center h-12 rounded-full bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 active:scale-[0.98] transition-all disabled:opacity-40 shadow-md shadow-primary/20"
          >
            Next
          </button>
          <button type="button" onClick={onNo} className="flex items-center justify-center px-6 h-12 rounded-full border border-border bg-card text-sm font-medium text-muted-foreground hover:text-foreground hover:border-foreground/40 active:scale-[0.98] transition-all">Skip</button>
        </div>
      );
    }
    const yesLabel = card.kind === 'trigger' ? 'Activate' : 'Yes';
    return (
      <div className="flex items-center gap-3 py-3">
        <button type="button" onClick={onYes} className="flex-1 flex items-center justify-center h-12 rounded-full bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 active:scale-[0.98] transition-all shadow-md shadow-primary/20">{yesLabel}</button>
        <button type="button" onClick={onNo} className="flex items-center justify-center px-6 h-12 rounded-full border border-border bg-card text-sm font-medium text-muted-foreground hover:text-foreground hover:border-foreground/40 active:scale-[0.98] transition-all">No</button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background">
      <Header title="Home" trailing={<><MessagesBell /><NotificationsBell /></>} />

      <main ref={mainRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {!card && !exhausted && <CardSkeleton />}
        {!card && exhausted && (
          <SuperconnectorBubble
            large
            godmother={godmother}
            narration={
              godmother
                ? me?.firstName
                  ? `I'm out for a bit, ${me.firstName} — back soon with more for you.`
                  : `I'm out for a bit — back soon with more for you.`
                : null
            }
            fallback="All caught up. Back soon with more for you."
          />
        )}

        <AnimatePresence mode="wait" onExitComplete={() => setTossing(false)}>
          {card && (
            <motion.div
              key={card.key}
              variants={tossing ? TOSS_VARIANTS : CARD_VARIANTS}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.26, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="space-y-3 touch-pan-y"
            >
              {renderCard(card)}
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      <footer
        className="shrink-0 bg-background/95 backdrop-blur-sm border-t border-border px-4"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {renderFooter()}
      </footer>

      <BottomSheet
        open={!!confirm}
        onClose={() => confirm?.onClose()}
        title={godmother?.firstName ?? 'Your Underling'}
        subtitle={godmother?.firstName ? 'Your Underling' : undefined}
        leading={<SuperconnectorAvatar godmother={godmother} size={6} />}
        fullHeight={confirm?.tall ?? false}
        footer={confirm?.tall ? <ConfirmFooter confirm={confirm} /> : undefined}
      >
        {confirm && <ConfirmContent key={confirm.title ?? confirm.message ?? ''} confirm={confirm} />}
      </BottomSheet>

      <BottomSheet
        open={seriesSheetOpen}
        onClose={dismissSeriesSheet}
        title={godmother?.firstName ?? 'Your Underling'}
        subtitle={godmother?.firstName ? 'Your Underling' : undefined}
        leading={<SuperconnectorAvatar godmother={godmother} size={6} />}
      >
        <div className="px-4 pt-4 pb-2 space-y-3">
          {seriesStage === 'pick' && card?.kind === 'event' && (
            <>
              <p className="text-[15px] text-foreground/80 leading-relaxed">
                Pick a time that works — I'll send the details by email.
              </p>
              <ul className="space-y-1.5">
                {(card.data.upcomingSessions ?? []).map((slot) => {
                  const soldOut = slot.capacity != null && slot.participantCount >= slot.capacity;
                  const seatsLabel = soldOut ? 'Sold out' : 'A few seats left';
                  const time = new Date(slot.startsAt).toLocaleString('en-GB', {
                    weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true,
                  });
                  return (
                    <li key={slot.id}>
                      <button
                        type="button"
                        disabled={soldOut}
                        onClick={() => handleSelectSlot(slot)}
                        className={`w-full flex items-center justify-between h-12 px-4 rounded-full border text-[14px] transition-all ${
                          soldOut
                            ? 'border-border bg-muted/40 text-muted-foreground/60 cursor-not-allowed'
                            : 'border-border bg-card text-foreground hover:bg-muted active:scale-[0.98]'
                        }`}
                      >
                        <span className="font-medium">{time}</span>
                        <span className={soldOut ? 'italic' : 'text-muted-foreground'}>{seatsLabel}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              <p className="text-[12px] text-muted-foreground/70 italic">Times shown in your local time zone.</p>
              {!card.data.hasInterest && (
                <button
                  type="button"
                  onClick={() => void handleNotifyMe()}
                  disabled={seriesNotifying}
                  className="w-full flex items-center justify-center h-12 rounded-full border border-border bg-card text-sm font-semibold text-foreground hover:bg-muted active:scale-[0.98] transition-all disabled:opacity-60"
                >
                  {seriesNotifying ? 'Saving…' : 'Notify me when new slots open up'}
                </button>
              )}
              {seriesError ? (
                <p className="text-[13px] text-destructive">{seriesError}</p>
              ) : null}
            </>
          )}
          {seriesStage === 'confirm_email' && seriesSlot && (
            <>
              <p className="text-[15px] text-foreground/80 leading-relaxed">
                You picked{' '}
                <span className="font-semibold">
                  {new Date(seriesSlot.startsAt).toLocaleString('en-GB', {
                    weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true,
                  })}
                </span>
                . Where should I send the calendar invite?
              </p>
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                value={seriesEmail}
                onChange={(e) => setSeriesEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full h-12 px-4 rounded-full border border-border bg-card text-[15px] outline-none focus:border-primary"
              />
              {seriesEmailError ? (
                <p className="text-[13px] text-destructive">{seriesEmailError}</p>
              ) : null}
              {seriesError ? (
                <p className="text-[13px] text-destructive">{seriesError}</p>
              ) : null}
              <button
                type="button"
                onClick={() => void handleConfirmEmail()}
                className="w-full h-12 rounded-full bg-primary text-primary-foreground text-sm font-semibold active:scale-[0.98] transition-all"
              >
                Send invite & confirm RSVP
              </button>
            </>
          )}
          {seriesStage === 'reserving' && (
            <p className="text-[15px] text-foreground/80 leading-relaxed">Reserving your seat…</p>
          )}
          {seriesStage === 'all_sold_out' && (
            <>
              <p className="text-[15px] text-foreground/80 leading-relaxed">
                {card?.kind === 'event' && card.data.hasInterest
                  ? "All seats are taken right now — I'll let you know as soon as new slots open up."
                  : 'All seats are taken right now. Want me to let you know when new slots open up?'}
              </p>
              {seriesError ? (
                <p className="text-[13px] text-destructive">{seriesError}</p>
              ) : null}
              {card?.kind === 'event' && !card.data.hasInterest && (
                <button
                  type="button"
                  onClick={() => void handleNotifyMe()}
                  disabled={seriesNotifying}
                  className="w-full flex items-center justify-center h-12 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 active:scale-[0.98] shadow-md shadow-primary/20 transition-all disabled:opacity-60"
                >
                  {seriesNotifying ? 'Saving…' : 'Notify me when new slots open up'}
                </button>
              )}
            </>
          )}
        </div>
      </BottomSheet>

      <BottomSheet
        open={initiativeSheetOpen}
        onClose={() => !initiativeSubmitting && setInitiativeSheetOpen(false)}
        title={godmother?.firstName ?? 'Your Underling'}
        subtitle={godmother?.firstName ? 'Your Underling' : undefined}
        leading={<SuperconnectorAvatar godmother={godmother} size={6} />}
        fullHeight
      >
        {card?.kind === 'initiative' && (
          <InitiativeForm
            initiative={card.data}
            submitting={initiativeSubmitting}
            onSubmit={handleSubmitInitiative}
          />
        )}
      </BottomSheet>

      <BottomSheet
        open={helpSheetOpen}
        onClose={closeHelpSheetAndAdvance}
        title={godmother?.firstName ?? 'Your Underling'}
        subtitle={godmother?.firstName ? 'Your Underling' : undefined}
        leading={<SuperconnectorAvatar godmother={godmother} size={6} />}
      >
        {card?.kind === 'help' && (
          <div className="px-4 pt-3 pb-2 space-y-3">
            <p className="text-[15px] text-foreground/85 leading-relaxed">
              Share this with anyone you think might know the right person.
            </p>
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <div className="w-full aspect-[1.91/1] bg-primary/10 flex items-center justify-center">
                <img
                  src={LOGO_MARK_PNG}
                  alt="Underlings"
                  className="w-20 h-20 object-contain"
                />
              </div>
              <div className="px-4 py-3 space-y-1">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {(() => {
                    try { return new URL(card.data.shareUrl).hostname; } catch { return ''; }
                  })()}
                </p>
                <p className="text-[14px] font-semibold text-foreground leading-snug">
                  {card.data.shortTitle}
                </p>
                <p className="text-[13px] text-muted-foreground leading-snug">
                  {card.data.description}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleHelpShare}
              disabled={helpSharing}
              className="w-full flex items-center justify-center h-12 rounded-full bg-primary text-primary-foreground font-semibold text-sm active:scale-[0.98] transition-all disabled:opacity-60"
            >
              {helpCopied ? 'Copied — paste anywhere to share' : 'Share'}
            </button>
          </div>
        )}
      </BottomSheet>

      <BottomSheet
        open={!!coalitionSheet}
        onClose={() => !coalitionSubmitting && setCoalitionSheet(null)}
        title={godmother?.firstName ?? 'Your Underling'}
        subtitle={godmother?.firstName ? 'Your Underling' : undefined}
        leading={<SuperconnectorAvatar godmother={godmother} size={6} />}
      >
        {coalitionSheet && (
          <div className="px-4 pt-3 pb-2 space-y-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-primary/90">
                Joining Coalition
              </p>
              <p className="text-[16px] font-semibold text-foreground leading-snug mt-1">
                {coalitionSheet.displayTitle ?? coalitionSheet.name}
              </p>
            </div>
            <p className="text-[13px] text-muted-foreground leading-snug">
              How would you like to contribute? Tick whatever applies — we'll
              come to you with specific asks that match.
            </p>
            <div className="space-y-2">
              {[
                { id: 'skills', label: 'Skills & expertise' },
                { id: 'intros', label: 'Intros & connections' },
                { id: 'funds', label: 'Funds or donations-in-kind' },
                { id: 'other', label: 'Other / let me elaborate' },
              ].map((o) => {
                const checked = coalitionSelected.has(o.id);
                return (
                  <label
                    key={o.id}
                    className="flex items-start gap-3 cursor-pointer select-none rounded-xl border border-border bg-card px-4 py-3 active:scale-[0.99] transition-transform"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleCoalitionOption(o.id)}
                      className="mt-0.5 w-4 h-4 accent-primary"
                    />
                    <span className="text-[14px] text-foreground/90 leading-snug">
                      {o.label}
                    </span>
                  </label>
                );
              })}
            </div>
            {coalitionSelected.has('other') && (
              <textarea
                value={coalitionOtherText}
                onChange={(e) => setCoalitionOtherText(e.target.value)}
                placeholder="Tell us how you'd like to contribute…"
                rows={3}
                className="w-full rounded-xl border border-border bg-card px-4 py-3 text-[14px] leading-snug text-foreground/90 placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            )}
            <button
              type="button"
              onClick={handleCoalitionSubmit}
              disabled={coalitionSubmitting}
              className="w-full flex items-center justify-center h-12 rounded-full bg-primary text-primary-foreground font-semibold text-sm active:scale-[0.98] transition-all disabled:opacity-40"
            >
              {coalitionSubmitting ? 'Joining…' : 'Count me in'}
            </button>
          </div>
        )}
      </BottomSheet>

      <BottomSheet
        open={!!mediaSheet}
        onClose={() => setMediaSheet(null)}
        title={godmother?.firstName ?? 'Your Underling'}
        subtitle={godmother?.firstName ? 'Your Underling' : undefined}
        leading={<SuperconnectorAvatar godmother={godmother} size={6} />}
      >
        {mediaSheet && (
          <div className="px-4 pt-3 pb-2 space-y-4">
            <p className="text-[15px] text-foreground/85 leading-relaxed">
              Great! I'll show you more like this in the future.
            </p>
            <div className="space-y-2">
              <button
                type="button"
                onClick={handleMediaConsume}
                className="w-full flex items-center justify-center h-11 rounded-full border border-border bg-card text-sm font-medium text-foreground active:scale-[0.99] transition-all"
              >
                {mediaConsumeLabel(mediaSheet)}
              </button>
              <button
                type="button"
                onClick={handleMediaSaveForLater}
                className="w-full flex items-center justify-center h-11 rounded-full border border-border bg-card text-sm font-medium text-foreground active:scale-[0.99] transition-all"
              >
                Save for later
              </button>
              <button
                type="button"
                onClick={handleMediaDone}
                className="w-full flex items-center justify-center h-12 rounded-full bg-primary text-primary-foreground font-semibold text-sm active:scale-[0.98] transition-all shadow-md shadow-primary/20"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </BottomSheet>

      <BottomSheet
        open={!!leagueSheet}
        onClose={() => !leagueSubmitting && setLeagueSheet(null)}
        title={godmother?.firstName ?? 'Your Underling'}
        subtitle={godmother?.firstName ? 'Your Underling' : undefined}
        leading={<SuperconnectorAvatar godmother={godmother} size={6} />}
      >
        {leagueSheet && (
          <div className="px-4 pt-3 pb-2 space-y-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-primary/90">
                Joining League
              </p>
              <p className="text-[16px] font-semibold text-foreground leading-snug mt-1">
                {leagueSheet.displayTitle ?? leagueSheet.name}
              </p>
            </div>
            {(leagueSheet.candidateTeams ?? []).length > 0 && (
              <>
                <p className="text-[15px] font-semibold text-foreground leading-snug">
                  Which teams will you represent?
                </p>
                <p className="text-[13px] text-muted-foreground leading-snug">
                  Tick the alumni networks you'd like to play for. You can
                  represent more than one.
                </p>
                <div className="space-y-2">
                  {(leagueSheet.candidateTeams ?? []).map((t) => {
                    const checked = leagueSelected.has(t.circleId);
                    return (
                      <label
                        key={t.circleId}
                        className="flex items-start gap-3 cursor-pointer select-none rounded-xl border border-border bg-card px-4 py-3 active:scale-[0.99] transition-transform"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleLeagueTeam(t.circleId)}
                          className="mt-0.5 w-4 h-4 accent-primary"
                        />
                        <span className="text-[14px] text-foreground/90 leading-snug">
                          {t.existingMemberCount > 0
                            ? `${t.name} (${t.existingMemberCount} already in)`
                            : t.name}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </>
            )}
            <button
              type="button"
              onClick={handleLeagueConfirm}
              disabled={
                leagueSubmitting ||
                ((leagueSheet.candidateTeams ?? []).length > 0 &&
                  leagueSelected.size === 0)
              }
              className="w-full flex items-center justify-center h-12 rounded-full bg-primary text-primary-foreground font-semibold text-sm active:scale-[0.98] transition-all disabled:opacity-40"
            >
              {leagueSubmitting ? 'Joining…' : 'Count me in'}
            </button>
          </div>
        )}
      </BottomSheet>

      <BookReaderSheet
        book={bookReader}
        open={!!bookReader}
        onClose={() => setBookReader(null)}
      />

    </div>
  );
}
